import { Component, Inject, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SavedFilters } from '../../../models/SavedFilters';
import { CustomCodingFieldsManager } from 'src/app/models/review/custom-coding-fields-manager';
import { KeyHandlerDirective } from '../../../directives/key-handler.directive';
import { ReviewSetService } from 'src/app/services/review-set.service';
import { ReviewSet } from 'src/app/models/review/review-set.model';
import { BriefcaseJob } from 'src/app/models/review/briefcase-job.model';
import { DocService } from 'src/app/services/doc.service';
import { Custodian } from 'src/app/models/review/custodian.model';
import { CustodianService } from 'src/app/services/custodian.service';
import { FilterExpanderState } from 'src/app/models/review/filter-expander-state';
import { UserResource } from '../../../models/resource/user.resource';
import { DocCodingResponsiveness } from '../../../enums/doc-coding-responsiveness.enum';
import { UserManager } from '../../../managers/user.manager';
import { UploadBatchesSdk } from "../../../sdks/upload-batches.sdk";
import { Upload } from "@models/ui/upload.model";
import { ProductionJobView } from '@models/ui/production-job-view.model';
import { ProductionJobSdk } from 'src/app/sdks/production-job.sdk';
import { BriefcaseJobSdk } from "../../../sdks/briefcase-job.sdk";

@Component({
    selector: 'lex-select-filter',
    templateUrl: './select-filter.component.html',
    styleUrls: ['./select-filter.component.scss'],
    standalone: false
})
export class SelectFilterComponent extends KeyHandlerDirective implements OnInit {
	/**
	 * Selection box placeholder option representing an empty value.
	 * (e.g. will match a doc with no source or a doc with no type if selected)
	 *  */
	public static readonly blank = '[Blank]';
	public docCodingResponsiveness = DocCodingResponsiveness;

	filter: SavedFilters = {};

	beforeDate: Date;
	afterDate: Date;
	masterDateBeforeDate: Date;
	masterDateAfterDate: Date;
	uploadedBeforeDate: Date;
	uploadedAfterDate: Date;

	// The following fields are bound to the UI and represent selected elements
	// (empty if nothing is selected - each element being a selected item)
	reviewedBy: string[];
	docSource: string[];
	docType: string[];

	isUnsupported: boolean;
	isFailedToConvert: boolean;

	// Each of these objects is composed of key/value pairs:
	// The key is the GUID field ID and the value is the appropriate value for that type (boolean or text)
	dynamicCodingFilterNot = {};
	dynamicCodingFilterTextbox = {};
	dynamicCodingFilterCheckbox = {};
	dynamicCodingFilterSharedCheckbox = {};

	ccfManager: CustomCodingFieldsManager;

	custodians: Custodian[];
	uploaders: UserResource[];
	uploadBatches: Upload[];
	briefcases: BriefcaseJob[];
	reviewSets: ReviewSet[];
	productionJobs: ProductionJobView[];
	reviewers: UserResource[];
	docSources: string[] = [];
	docTypes: string[] = [];

	filterExpanderState: FilterExpanderState;

	constructor(
		private dialogRef: MatDialogRef<SelectFilterComponent>,
		@Inject(MAT_DIALOG_DATA) data,
		private uploadBatchSdk: UploadBatchesSdk,
		private productionJobSdk: ProductionJobSdk,
		private reviewSetService: ReviewSetService,
		private docService: DocService,
		private userManager: UserManager,
		private custodianService: CustodianService,
		private briefcaseJobSdk: BriefcaseJobSdk
	) {
		super();
		this.ccfManager = data.ccfManager;
		this.loadExistingFilter(data);
		this.filterExpanderState = data.expanderState;
	}

	ngOnInit(): void {
		this.custodianService.getCustodians().subscribe(data => this.custodians = data);
		this.userManager.getUploaders().subscribe(data => this.uploaders = data);
		this.userManager.getReviewers().subscribe(data => this.reviewers = data);
		this.uploadBatchSdk.get({skip: null, take: null, orderByColumn: 'batchTitle', orderByDirection: 'asc'})
			.subscribe(data => this.uploadBatches = data.items);
		this.briefcaseJobSdk.get().subscribe(data => this.briefcases = data.items);
		this.productionJobSdk.get().subscribe(data => this.productionJobs = data);
		this.reviewSetService.getReviewSets().subscribe(data => this.reviewSets = data);
		this.docService.getDistinctDocSources().subscribe(data => data.forEach(ds => {
			if (ds === '')
				this.docSources.push(SelectFilterComponent.blank);
			else this.docSources.push(ds);
		}));
		this.docService.getDistinctDocTypes().subscribe(data => data.forEach(dt => {
			if (dt === '')
				this.docTypes.push(SelectFilterComponent.blank);
			else this.docTypes.push(dt);
		}));
	}

	private loadExistingFilter(data: { filter: SavedFilters; }) {
		if (!data.filter) return;

		this.filter = data.filter;

		this.beforeDate = this.getDate(this.filter.BeforeDate);
		this.afterDate = this.getDate(this.filter.AfterDate);
		this.masterDateBeforeDate = this.getDate(this.filter.MasterDateBeforeDate);
		this.masterDateAfterDate = this.getDate(this.filter.MasterDateAfterDate);
		this.uploadedBeforeDate = this.getDate(this.filter.UploadedBeforeDate);
		this.uploadedAfterDate = this.getDate(this.filter.UploadedAfterDate);

		if (this.filter.Reviewers) this.reviewedBy = this.filter.Reviewers.split(',');

		if (this.filter.PlaceholderNotConverted)
			this.isUnsupported = this.filter.PlaceholderNotConverted === 1;

		if (this.filter.PlaceholderFailedToConvert)
			this.isFailedToConvert = this.filter.PlaceholderFailedToConvert === 2;

		if (this.filter.DocSource || this.filter.DocSource === '') {
			this.docSource = [];
			const sources = this.filter.DocSource.split(',');
			sources.forEach(s => {
				if (!s) this.docSource.push(SelectFilterComponent.blank);
				else this.docSource.push(s);
			});
		}

		if (this.filter.DocType || this.filter.DocType === '') {
			this.docType = [];
			const types = this.filter.DocType.split(',');
			types.forEach(t => {
				if (!t) this.docType.push(SelectFilterComponent.blank);
				else this.docType.push(t);
			});
		}

		if (this.filter.DynamicCodingFilterNot) {
			this.filter.DynamicCodingFilterNot.split(',').forEach(id => this.dynamicCodingFilterNot[id] = true);
		}

		if (this.filter.DynamicCodingFilterTextbox) {
			this.filter.DynamicCodingFilterTextbox.split(',').forEach(val => {
				const [id, text] = val.split('~');
				this.dynamicCodingFilterTextbox[id] = text;
			});
		}

		if (this.filter.DynamicCodingFilterCheckbox) {
			this.filter.DynamicCodingFilterCheckbox.split(',').forEach(id => this.dynamicCodingFilterCheckbox[id] = true);
		}

		if (this.filter.DynamicCodingFilterSharedCheckbox) {
			this.filter.DynamicCodingFilterSharedCheckbox.split(',').forEach(id => this.dynamicCodingFilterSharedCheckbox[id] = true);
		}
	}

	private getDate(date: string): Date {
		if (!date) return null;
		return new Date(date);
	}

	placeholderUnsupported(event: MatCheckboxChange) {
		this.filter.PlaceholderNotConverted = event.checked ? 1 : undefined;
	}

	placeholderFailedToConvert(event: MatCheckboxChange) {
		this.filter.PlaceholderFailedToConvert = event.checked ? 2 : undefined;
	}

	ok() {
		this.filter.BeforeDate = this.getDateString(this.beforeDate);
		this.filter.AfterDate = this.getDateString(this.afterDate);
		this.filter.MasterDateBeforeDate = this.getDateString(this.masterDateBeforeDate);
		this.filter.MasterDateAfterDate = this.getDateString(this.masterDateAfterDate);
		this.filter.UploadedBeforeDate = this.getDateString(this.uploadedBeforeDate);
		this.filter.UploadedAfterDate = this.getDateString(this.uploadedAfterDate);

		if (this.filter.PlaceholderNotConverted) this.filter.PlaceholderNotConverted = 1;
		if (this.filter.PlaceholderFailedToConvert) this.filter.PlaceholderFailedToConvert = 2;

		if (this.reviewedBy) this.filter.Reviewers = this.reviewedBy.join(',');
		if (this.docSource) this.filter.DocSource = this.docSource.join(',').replace(SelectFilterComponent.blank, '');
		if (this.docType) this.filter.DocType = this.docType.join(',').replace(SelectFilterComponent.blank, '');

		this.filter.DynamicCodingFilterNot = this.toCsv(this.dynamicCodingFilterNot);
		this.filter.DynamicCodingFilterTextbox = this.toCsv(this.dynamicCodingFilterTextbox, true);
		this.filter.DynamicCodingFilterCheckbox = this.toCsv(this.dynamicCodingFilterCheckbox);
		this.filter.DynamicCodingFilterSharedCheckbox = this.toCsv(this.dynamicCodingFilterSharedCheckbox);

		let anyPropIsPopulated = false;
		for (const prop in this.filter) {
			if ((prop === 'DocType' && this.filter[prop] === '') ||
				(prop === 'DocSource' && this.filter[prop] === '') ||
				this.filter[prop]) {
				anyPropIsPopulated = true;
			} else {
				// Clear out any boolean properties that are false (this will make sure they're not serialized to JSON)
				this.filter[prop] = undefined;
			}
		}

		this.dialogRef.close({
			isCancelled: false,
			filter: anyPropIsPopulated ? this.filter : null,
			expanderState: this.filterExpanderState
		});
	}

	cancel() {
		this.dialogRef.close({
			isCancelled: true,
			filter: null,
			expanderState: this.filterExpanderState
		});
	}

	private getDateString(date: Date): string {
		if (!date) return undefined;
		return date.toLocaleDateString();
	}

	private toCsv(obj, includeValue = false): string {
		const res: string[] = [];

		for (const fieldId in obj) {
			if (obj[fieldId]) { // Only save fields that have a positive value (e.g. True or some text - not False or empty strings)
				if (includeValue) res.push(`${fieldId}~${obj[fieldId]}`);
				else res.push(fieldId);
			}
		}

		return res.length > 0 ? res.join(',') : undefined;
	}
}
